import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';

import { AdminAgenciesService } from '../../../core/admin/admin-agencies.service';
import { AdminAgency } from '../../../core/admin/admin-agencies.types';
import { IconComponent } from '../../../shared/icon/icon';
import { DashPipe } from '../../../shared/pipes/dash.pipe';
import { readError } from '../../../core/http/read-error';

@Component({
  selector: 'app-admin-agencies',
  imports: [
    RouterLink,
    ReactiveFormsModule,
    DatePipe,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    IconComponent,
    DashPipe,
  ],
  templateUrl: './admin-agencies.html',
  styleUrl: './admin-agencies.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminAgenciesComponent implements OnInit {
  private readonly service = inject(AdminAgenciesService);
  private readonly fb = inject(FormBuilder);
  private readonly snack = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly agencies = signal<AdminAgency[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly addOpen = signal(false);
  protected readonly submitting = signal(false);

  protected readonly createForm = this.fb.group({
    name: ['', Validators.required],
    reg_no: [''],
    admin_first_name: [''],
    admin_last_name: [''],
    admin_email: ['', [Validators.required, Validators.email]],
  });

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.isLoading.set(true);
    this.service
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.agencies.set(res.agencies);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  protected toggleAdd(): void {
    this.addOpen.update((v) => !v);
    if (!this.addOpen()) {
      this.createForm.reset();
    }
  }

  protected submitCreate(): void {
    if (this.createForm.invalid || this.submitting()) {
      this.createForm.markAllAsTouched();
      return;
    }
    this.submitting.set(true);
    const v = this.createForm.getRawValue();
    this.service
      .create({
        name: v.name!,
        reg_no: v.reg_no ?? '',
        admin_email: v.admin_email!,
        admin_first_name: v.admin_first_name ?? '',
        admin_last_name: v.admin_last_name ?? '',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (agency) => {
          this.agencies.update((list) =>
            [...list, agency].sort((a, b) => a.name.localeCompare(b.name)),
          );
          this.submitting.set(false);
          this.addOpen.set(false);
          this.createForm.reset();
          this.snack.open(`${agency.name} created — the admin was emailed an invite.`, 'Dismiss', {
            duration: 5000,
          });
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.submitting.set(false);
          this.snack.open(readError(err), 'Dismiss', { duration: 6000 });
          this.cdr.markForCheck();
        },
      });
  }

  protected toggleActive(agency: AdminAgency): void {
    this.service
      .update(agency.short_id, { is_active: !agency.is_active })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.agencies.update((list) =>
            list.map((a) => (a.short_id === updated.short_id ? updated : a)),
          );
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.snack.open(readError(err), 'Dismiss', { duration: 6000 });
        },
      });
  }
}
